import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { PropertyGroupDescription, SortDescription } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellRangeEventArgs, CellType, FlexGrid, GridPanel, GroupRow } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    FabDraftingQueue,
    FabJob,
    FabPiece,
    FabProcessStep,
    FabStampStepRequest,
    FeatureType,
    Grid,
    ListItem,
    replaceListItemsIfChanged,
} from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridColumnGroupComponent } from '../../shared/reusable-components/grid-column-group/grid-column-group.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

@Component({
    selector: 'app-fab-drafting-queue',
    templateUrl: './fab-drafting-queue.component.html',
    standalone: true,
    imports: [
        FormsModule,
        WjInputModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridColumnGroupComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class FabDraftingQueueComponent extends BaseComponent {
    grid: Grid<FabPiece>
    recentGrid: Grid<FabPiece>
    jobs: FabJob[] = []
    jobOptions: ListItem<string>[] = []
    selectedJobId = ''
    private appliedJobId = ''
    processSteps: FabProcessStep[] = []
    draftingSteps: FabProcessStep[] = []
    ifaStepId = ''
    iffStepId = ''

    constructor() {
        super()
        this.grid = new Grid<FabPiece>(this.services, 'id', this)
            .withColumnFilters()
            .filterBy(item => [item.jobDisplayName, item.markCode, item.name, item.scope, item.nextStepName, item.riskLevel])
            .isReadOnly(false)
            .editOnDoubleClick(false)

        this.recentGrid = new Grid<FabPiece>(this.services, 'id', this)
            .withColumnFilters()
            .filterBy(item => [item.jobDisplayName, item.markCode, item.name, item.scope])
            .isReadOnly(true)

        this.grid.initialized.on(() => {
            this.grid.flexGrid.cellEditEnding.addHandler(this.cellEditEnding.bind(this))
            this.grid.flexGrid.cellEditEnded.addHandler(this.cellEditEnded.bind(this))
            this.applyJobGrouping()
        })

        this.refresh()
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FabDraftingQueueEdit])
    }

    onJobChanged() {
        if (this.selectedJobId === this.appliedJobId) return
        this.refresh(false)
    }

    refresh(showSpinner = true) {
        const requestedJobId = this.selectedJobId
        let params = new HttpParams()
        if (requestedJobId) {
            params = params.set('fabJobId', requestedJobId)
        }

        this.cancelableRequest(
            '',
            showSpinner,
            this.services.data
                .load(showSpinner, showSpinner, this.services.data.get<FabDraftingQueue>('api/FabViews/DraftingQueue', params))
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        const view = result[0]
                        this.jobs = view.jobs || []
                        this.processSteps = view.processSteps || []
                        this.draftingSteps = this.processSteps
                            .filter(s => s.category === 'Drafting' && s.active)
                            .sort((a, b) => a.sortOrder - b.sortOrder)
                        this.ifaStepId = this.draftingSteps[0]?.id || ''
                        this.iffStepId = this.draftingSteps[this.draftingSteps.length - 1]?.id || ''

                        this.jobOptions = replaceListItemsIfChanged(this.jobOptions, [
                            new ListItem('', '(All jobs)'),
                            ...this.jobs.map(j => new ListItem(j.id, j.displayName || j.jobDescription || j.name || '')),
                        ])
                        this.appliedJobId = requestedJobId
                        if (this.selectedJobId !== requestedJobId) {
                            this.selectedJobId = requestedJobId
                        }

                        const jobIndexById = new Map(this.jobs.map((j, i) => [j.id, i]))
                        this.grid.items = (view.pending || []).map(p => this.flattenDrafting(p, jobIndexById))
                        this.recentGrid.items = (view.recentlyApproved || []).map(p => this.flattenDrafting(p, jobIndexById))
                        this.applyJobGrouping()
                        this.grid.canEdit = this.canEdit
                    },
                }),
        )
    }

    private flattenDrafting(piece: FabPiece, jobIndexById: Map<string, number>): FabPiece {
        const row = piece as FabPiece & Record<string, unknown>
        const ifa = (piece.steps || []).find(s => s.fabProcessStepId === this.ifaStepId)
        const iff = (piece.steps || []).find(s => s.fabProcessStepId === this.iffStepId)
        row['ifaNa'] = !!ifa?.isNotApplicable
        row['iffNa'] = !!iff?.isNotApplicable
        row['ifaDate'] = ifa?.isNotApplicable || !ifa?.actualDate ? null : new Date(ifa.actualDate)
        row['iffDate'] = iff?.isNotApplicable || !iff?.actualDate ? null : new Date(iff.actualDate)
        row['ifaPlanned'] = ifa?.isNotApplicable || !ifa?.plannedDate ? null : new Date(ifa.plannedDate)
        row['iffPlanned'] = iff?.isNotApplicable || !iff?.plannedDate ? null : new Date(iff.plannedDate)
        const jobIndex = jobIndexById.get(piece.fabJobId)
        const job = jobIndex != null ? this.jobs[jobIndex] : undefined
        piece.jobDisplayName = job?.displayName || job?.jobDescription || job?.name || '(No job)'
        piece.jobSortOrder = jobIndex != null ? jobIndex : Number.MAX_SAFE_INTEGER
        return piece
    }

    private applyJobGrouping() {
        if (!this.grid.isInitialized) return
        const view = this.grid.view
        view.beginUpdate()
        view.sortDescriptions.clear()
        view.sortDescriptions.push(new SortDescription('jobSortOrder', true))
        view.groupDescriptions.clear()
        view.groupDescriptions.push(new PropertyGroupDescription('jobDisplayName'))
        view.endUpdate()
        this.grid.flexGrid.groupHeaderFormat = '{name} ({count:n0} items)'
    }

    cellEditEnding(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        const item = flexGrid.rows[args.row]?.dataItem as (FabPiece & Record<string, unknown>) | undefined
        const binding = flexGrid.columns[args.col]?.binding
        if (!item?.id || !binding) {
            args.cancel = true
            return
        }
        if ((binding === 'ifaDate' || binding === 'ifaPlanned') && item['ifaNa']) args.cancel = true
        if ((binding === 'iffDate' || binding === 'iffPlanned') && item['iffNa']) args.cancel = true
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (!this.canEdit) return
        const item = flexGrid.rows[args.row]?.dataItem as FabPiece & Record<string, unknown>
        const binding = flexGrid.columns[args.col]?.binding
        if (!item || !binding) return

        if ((binding === 'ifaDate' && item['ifaNa']) || (binding === 'iffDate' && item['iffNa'])) return

        let stepId = ''
        if (binding === 'ifaDate') stepId = this.ifaStepId
        else if (binding === 'iffDate') stepId = this.iffStepId
        else return

        const value = item[binding] as Date | null
        const body: FabStampStepRequest = {
            fabPieceId: item.id,
            fabProcessStepId: stepId,
            actualDate: value,
            clear: value == null,
            note: null,
        }

        this.services.data.post('api/FabViews/StampStep', body).subscribe({
            next: () => this.refresh(false),
            error: () => this.services.notification.error('Failed to stamp step date.'),
        })
    }

    itemFormatter = (panel: GridPanel, r: number, c: number, cell: HTMLDivElement) => {
        if (panel.cellType !== CellType.Cell) return
        if (panel.rows[r] instanceof GroupRow) return
        const item = panel.rows[r]?.dataItem as FabPiece
        const binding = panel.columns[c]?.binding
        if (!item || !binding) return
        cell.classList.remove('red-cell', 'yellow-cell')
        const row = item as FabPiece & Record<string, unknown>
        if ((binding === 'ifaDate' || binding === 'ifaPlanned') && row['ifaNa']) {
            cell.textContent = 'NA'
            return
        }
        if ((binding === 'iffDate' || binding === 'iffPlanned') && row['iffNa']) {
            cell.textContent = 'NA'
            return
        }
        if (binding === 'riskLevel' || binding === 'daysLate') {
            if (item.riskLevel === 'AtRisk' || item.riskLevel === 'Blocked') cell.classList.add('red-cell')
            else if (item.riskLevel === 'DueSoon') cell.classList.add('yellow-cell')
        }
        if ((binding === 'ifaDate' || binding === 'iffDate') && item.riskLevel === 'AtRisk' && item[binding] == null) {
            cell.classList.add('red-cell')
        }
    }
}
